import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { VictoryChart, VictoryLine, VictoryAxis } from "victory";

import Slider from "rc-slider";
import "rc-slider/assets/index.css";

import Network from "../neat/network";
import Specie from "../neat/specie";
import NetworkVis from "../components/NetworkVis";
import { GameProps } from "./util";

const Container = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  padding: 2rem 0;

  & > * {
    margin: 1rem;
  }
`;

const Card = styled.section`
  width: 450px;
  min-width: 300px;
  height: 450px;
  padding: 2rem;
  box-sizing: border-box;

  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);

  @media screen and (max-width: 420px) {
    height: auto;
    padding: 2rem 3rem 3rem 2rem;
  }
`;

const OptionsCard = styled(Card)`
  padding: 2rem 3rem 2rem 2rem;
`;

const CardTitle = styled.h1`
  margin: 0;
  color: #212529;
`;

const SliderContainer = styled.div`
  display: grid;
  grid-auto-rows: 2rem;
  grid-template-columns: 4fr 6fr;
  place-items: center start;

  margin-top: 2rem;
  grid-gap: 2rem 1rem;

  @media screen and (max-width: 420px) {
    grid-template-columns: 1fr;
    grid-gap: 0.5rem 0;
    place-items: center;
  }
`;

const SliderTitle = styled.h2`
  color: #495057;
  font-size: 1rem;
  font-weight: normal;

  margin: 0;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoList = styled.ul`
  margin: 1rem 0 0 0;
`;

const NetworkContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
`;

interface ViewPageProps<T> {
  options: T;
  Game: React.ComponentType<GameProps<T>>;
  data: {
    value: number;
    setter: React.Dispatch<React.SetStateAction<number>>;
    name: string;
    min: number;
    max: number;
    step?: number;
  }[];
}

export default function ViewPage<T>({ options, Game, data }: ViewPageProps<T>) {
  const [generation, setGeneration] = useState(1);
  const [speciesNum, setSpeciesNum] = useState(1);
  const [bestNetwork, setBestNetwork] = useState<Network | null>(null);
  const [scoreList, setScoreList] = useState<{ x: number; y: number }[]>([]);

  const onInit = useCallback(() => {
    setGeneration(1);
    setSpeciesNum(1);
    setBestNetwork(null);
    setScoreList([]);
  }, []);

  const onEachGeneration = useCallback((pool: Network[], species: Specie[]) => {
    setGeneration(generation + 1);
    setSpeciesNum(species.length);
    setBestNetwork(pool[0]);
    setScoreList(scoreList.concat({ x: generation, y: pool[0].getFitness() }));
  }, [generation, scoreList]);

  function scale(x: number) {
    const result = [];
    const offset = Math.max(1, Math.floor(x / 4));
    for (let i = 1; i <= x; i += offset) {
      result.push(i);
    }
    return result;
  }

  return (
    <Container>
      <Game
        options={options}
        onInit={onInit}
        onEachGeneration={onEachGeneration}
      />
      <OptionsCard>
        <CardTitle>Options</CardTitle>
        <SliderContainer>
          {data.map((d) => (
            <>
              <SliderTitle>{d.name}</SliderTitle>
              <Slider
                value={d.value}
                step={d.step}
                min={d.min}
                max={d.max}
                marks={{ [d.min]: d.min, [d.max]: d.max }}
                onChange={d.setter}
              />
            </>
          ))}
        </SliderContainer>
      </OptionsCard>
      <Card>
        <CardTitle>Evolve Info</CardTitle>
        <InfoContainer>
          <InfoList>
            <li> Generation : {generation} </li>
            <li> Species : {speciesNum} </li>
            <li> Best Score by generation </li>
          </InfoList>
          <VictoryChart>
            <VictoryAxis
              label="Generation"
              tickValues={scale(generation - 1)}
            />
            <VictoryAxis
              dependentAxis
              tickValues={scale(Math.max(...scoreList.map((o) => o.y)))}
            />
            <VictoryLine data={scoreList}></VictoryLine>
          </VictoryChart>
        </InfoContainer>
      </Card>
      <Card>
        <CardTitle>Best genome</CardTitle>
        <NetworkContainer>
          {bestNetwork ? (
            <NetworkVis network={bestNetwork} />
          ) : (
            <p>Not evaluated yet</p>
          )}
        </NetworkContainer>
      </Card>
    </Container>
  );
}
